import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Textarea } from './ui/textarea';
import { 
  FileText, Download, CheckCircle, Clock, AlertCircle, Plus, Upload, Eye, 
  Send, FileSignature, History, ChevronRight, Loader2, ExternalLink, 
  Shield, File, Trash2, MessageSquare, Edit3, Check, User, GitBranch,
  ChevronDown, ChevronUp
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContractWorkflowSection = ({ collaboration, onContractSigned, userRole }) => {
  const [contracts, setContracts] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showSignModal, setShowSignModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [signing, setSigning] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadDocType, setUploadDocType] = useState('other');
  
  // Review workflow states
  const [comments, setComments] = useState([]);
  const [versions, setVersions] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [submittingReview, setSubmittingReview] = useState(false);

  const collaborationId = collaboration?.collaboration?.id;
  const isPM = collaboration?.pm_company?.id === collaboration?.collaboration?.pm_company_id;

  useEffect(() => {
    if (collaborationId) {
      fetchContracts();
      fetchDocuments();
    }
  }, [collaborationId]);

  const fetchContracts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/collaborations/${collaborationId}/contracts`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setContracts(response.data.contracts || []);
    } catch (error) {
      // Try single contract endpoint as fallback
      try {
        const response = await axios.get(
          `${API}/collaborations/${collaborationId}/contract`,
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
        );
        if (response.data.contract) {
          setContracts([response.data.contract]);
        }
      } catch (e) {
        console.log('No contracts found');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchDocuments = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/collaborations/${collaborationId}/documents`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDocuments(response.data.documents || []);
    } catch (error) {
      console.log('No documents endpoint or empty');
    }
  };

  const handleSignContract = async () => {
    if (!agreedToTerms || !selectedContract) return;

    setSigning(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/contracts/${selectedContract.id}/sign`,
        { agreement_accepted: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Contract signed successfully!');
      setShowSignModal(false);
      setAgreedToTerms(false);
      fetchContracts();
      if (onContractSigned) onContractSigned();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sign contract');
    } finally {
      setSigning(false);
    }
  };

  const handleUploadDocument = async () => {
    if (!uploadFile) {
      toast.error('Please select a file');
      return;
    }

    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('document_type', uploadDocType);
      formData.append('collaboration_id', collaborationId);

      await axios.post(
        `${API}/collaborations/${collaborationId}/documents`,
        formData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          } 
        }
      );
      
      toast.success('Document uploaded successfully!');
      setShowUploadModal(false);
      setUploadFile(null);
      fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  // Review workflow functions
  const fetchComments = async (contractId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/contracts/${contractId}/comments`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setComments(response.data.comments || []);
    } catch (error) {
      console.log('Could not fetch comments');
    }
  };

  const fetchVersions = async (contractId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/contracts/${contractId}/versions`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setVersions(response.data.versions || []);
    } catch (error) {
      console.log('Could not fetch versions');
    }
  };

  const handleOpenReview = (contract) => {
    setSelectedContract(contract);
    setShowReviewModal(true);
    fetchComments(contract.id);
    fetchVersions(contract.id);
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !selectedContract) return;

    setSubmittingReview(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/contracts/${selectedContract.id}/comments`,
        { content: newComment, comment_type: 'suggestion' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Comment added successfully');
      setNewComment('');
      fetchComments(selectedContract.id);
    } catch (error) {
      toast.error('Failed to add comment');
    } finally {
      setSubmittingReview(false);
    }
  };

  const handleRequestModification = async () => {
    if (!selectedContract) return;

    setSubmittingReview(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/contracts/${selectedContract.id}/request-modification`,
        { message: newComment || 'Modification requested' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Modification request sent to the other party');
      setNewComment('');
      fetchContracts();
    } catch (error) {
      toast.error('Failed to request modification');
    } finally {
      setSubmittingReview(false);
    }
  };

  const handleApproveContract = async () => {
    if (!selectedContract) return;

    setSubmittingReview(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/contracts/${selectedContract.id}/approve`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Contract approved! Proceeding to signature.');
      fetchContracts();
      setShowReviewModal(false);
    } catch (error) {
      toast.error('Failed to approve contract');
    } finally {
      setSubmittingReview(false);
    }
  };

  const getStatusBadge = (contract) => {
    if (contract.status === 'fully_signed' || (contract.pm_signed && contract.rm_signed)) {
      return <Badge className="bg-green-100 text-green-800">✓ Fully Signed</Badge>;
    }
    if (contract.pm_signed || contract.rm_signed) {
      return <Badge className="bg-yellow-100 text-yellow-800">Awaiting Signature</Badge>;
    }
    return <Badge className="bg-blue-100 text-blue-800">Draft</Badge>;
  };

  const getContractTypeLabel = (type) => {
    const types = {
      'nda': 'NDA',
      'non_compete': 'Non-Compete',
      'regie': 'Time & Material',
      'time_material': 'Time & Material',
      'project_based': 'Fixed Price',
      'fixed_price': 'Fixed Price',
      'freelance': 'Freelance'
    };
    return types[type] || type;
  };

  const CONTRACT_STEPS = [
    { 
      key: 'nda', 
      label: 'NDA (Non-Disclosure)', 
      description: 'Protect confidential information',
      icon: Shield,
      required: true
    },
    { 
      key: 'non_compete', 
      label: 'Non-Compete Agreement', 
      description: 'Prevent competitive conflicts',
      icon: FileSignature,
      required: false
    },
    { 
      key: 'main', 
      label: 'Main Contract (T&M / Fixed)', 
      description: 'Define collaboration terms',
      icon: FileText,
      required: true
    }
  ];

  const getStepStatus = (stepKey) => {
    const contract = contracts.find(c => {
      if (stepKey === 'nda') return c.collaboration_type === 'nda' || c.template_type === 'nda';
      if (stepKey === 'non_compete') return c.collaboration_type === 'non_compete' || c.template_type === 'non_compete';
      if (stepKey === 'main') return ['regie', 'time_material', 'project_based', 'fixed_price', 'freelance'].includes(c.collaboration_type || c.template_type);
      return false;
    });

    if (!contract) return 'pending';
    if (contract.pm_signed && contract.rm_signed) return 'completed';
    if (contract.pm_signed || contract.rm_signed) return 'awaiting';
    return 'draft';
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 flex justify-center">
          <Loader2 className="w-6 h-6 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Contract Workflow Overview */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center">
                <FileSignature className="w-5 h-5 mr-2 text-[#62D84E]" />
                Contract Workflow
              </CardTitle>
              <CardDescription>
                Complete all required contracts to start the collaboration
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setShowUploadModal(true)}
              >
                <Upload className="w-4 h-4 mr-2" />
                Upload Document
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Progress Steps */}
          <div className="space-y-4">
            {CONTRACT_STEPS.map((step, index) => {
              const status = getStepStatus(step.key);
              const Icon = step.icon;
              const contract = contracts.find(c => {
                if (step.key === 'nda') return c.collaboration_type === 'nda' || c.template_type === 'nda';
                if (step.key === 'non_compete') return c.collaboration_type === 'non_compete' || c.template_type === 'non_compete';
                if (step.key === 'main') return ['regie', 'time_material', 'project_based', 'fixed_price', 'freelance'].includes(c.collaboration_type || c.template_type);
                return false;
              });

              return (
                <div 
                  key={step.key}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    status === 'completed' ? 'border-green-300 bg-green-50' :
                    status === 'awaiting' ? 'border-yellow-300 bg-yellow-50' :
                    status === 'draft' ? 'border-blue-300 bg-blue-50' :
                    'border-gray-200 bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        status === 'completed' ? 'bg-green-500 text-white' :
                        status === 'awaiting' ? 'bg-yellow-500 text-white' :
                        status === 'draft' ? 'bg-blue-500 text-white' :
                        'bg-gray-300 text-gray-600'
                      }`}>
                        {status === 'completed' ? (
                          <CheckCircle className="w-5 h-5" />
                        ) : status === 'awaiting' ? (
                          <Clock className="w-5 h-5" />
                        ) : (
                          <Icon className="w-5 h-5" />
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{step.label}</span>
                          {step.required && (
                            <Badge variant="outline" className="text-xs">Required</Badge>
                          )}
                        </div>
                        <p className="text-sm text-gray-500">{step.description}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {contract ? (
                        <>
                          {getStatusBadge(contract)}
                          <div className="flex gap-1">
                            {/* View Button */}
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => window.open(`${API}/contracts/${contract.id}/pdf`, '_blank')}
                              title="View PDF"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            
                            {/* Review Button */}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleOpenReview(contract)}
                              title="Review & Comments"
                            >
                              <MessageSquare className="w-4 h-4 mr-1" />
                              Review
                            </Button>
                            
                            {/* Sign Button - if not signed by current user */}
                            {((!isPM && !contract.rm_signed) || (isPM && !contract.pm_signed)) && (
                              <Button
                                size="sm"
                                variant="default"
                                style={{ backgroundColor: '#62D84E' }}
                                onClick={() => {
                                  setSelectedContract(contract);
                                  setShowSignModal(true);
                                }}
                              >
                                <FileSignature className="w-4 h-4 mr-1" />
                                Sign
                              </Button>
                            )}
                          </div>
                        </>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            const type = step.key === 'main' 
                              ? (collaboration.project?.commercial_model === 'Fixed Price' ? 'fixed_price' : 'time_material')
                              : step.key;
                            window.location.href = `/collaborations/${collaborationId}/create-contract?type=${type}`;
                          }}
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          Create
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Signature Details */}
                  {contract && (
                    <div className="mt-3 pt-3 border-t flex gap-6">
                      <div className="flex items-center gap-2 text-sm">
                        <span className="text-gray-500">PM:</span>
                        {contract.pm_signed ? (
                          <span className="flex items-center text-green-600">
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Signed {contract.pm_signed_at ? new Date(contract.pm_signed_at).toLocaleDateString() : ''}
                          </span>
                        ) : (
                          <span className="flex items-center text-yellow-600">
                            <Clock className="w-4 h-4 mr-1" />
                            Pending
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <span className="text-gray-500">RM:</span>
                        {contract.rm_signed ? (
                          <span className="flex items-center text-green-600">
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Signed {contract.rm_signed_at ? new Date(contract.rm_signed_at).toLocaleDateString() : ''}
                          </span>
                        ) : (
                          <span className="flex items-center text-yellow-600">
                            <Clock className="w-4 h-4 mr-1" />
                            Pending
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Uploaded Documents Section */}
      {documents.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center text-lg">
              <File className="w-5 h-5 mr-2" />
              Additional Documents
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {documents.map((doc, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-gray-500" />
                    <div>
                      <p className="font-medium text-sm">{doc.name || doc.filename}</p>
                      <p className="text-xs text-gray-500">{doc.document_type} • {doc.uploaded_by}</p>
                    </div>
                  </div>
                  <Button size="sm" variant="ghost">
                    <Download className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Sign Contract Modal */}
      <Dialog open={showSignModal} onOpenChange={setShowSignModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Sign Contract</DialogTitle>
            <DialogDescription>
              Review the contract carefully before signing
            </DialogDescription>
          </DialogHeader>

          {selectedContract && (
            <div className="space-y-4">
              <div className="p-4 bg-gray-50 rounded-lg max-h-64 overflow-y-auto">
                <h4 className="font-medium mb-2">Contract Summary</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="text-gray-500">Type:</span> {getContractTypeLabel(selectedContract.collaboration_type || selectedContract.template_type)}</div>
                  <div><span className="text-gray-500">Duration:</span> {selectedContract.duration_months} months</div>
                  <div><span className="text-gray-500">Rate:</span> {selectedContract.rate_per_day} {selectedContract.rate_currency}/day</div>
                  <div><span className="text-gray-500">Payment Terms:</span> {selectedContract.payment_terms}</div>
                </div>
                {selectedContract.content_text && (
                  <div className="mt-4 pt-4 border-t">
                    <p className="text-xs text-gray-600 whitespace-pre-wrap line-clamp-6">
                      {selectedContract.content_text?.substring(0, 500)}...
                    </p>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <Checkbox 
                  id="agree-terms"
                  checked={agreedToTerms}
                  onCheckedChange={(checked) => setAgreedToTerms(checked)}
                />
                <Label htmlFor="agree-terms" className="text-sm">
                  I have read and agree to the terms of this contract. I understand this constitutes a legally binding electronic signature.
                </Label>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowSignModal(false);
              setAgreedToTerms(false);
            }}>
              Cancel
            </Button>
            <Button 
              onClick={handleSignContract} 
              disabled={!agreedToTerms || signing}
              style={{ backgroundColor: '#62D84E' }}
            >
              {signing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <FileSignature className="w-4 h-4 mr-2" />
              )}
              Sign Contract
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Document Modal */}
      <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Document</DialogTitle>
            <DialogDescription>
              Upload additional documents for this collaboration
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Document Type</Label>
              <select
                className="w-full mt-1 p-2 border rounded-md"
                value={uploadDocType}
                onChange={(e) => setUploadDocType(e.target.value)}
              >
                <option value="signed_contract">Signed Contract (PDF)</option>
                <option value="amendment">Contract Amendment</option>
                <option value="sow">Statement of Work (SOW)</option>
                <option value="invoice">Invoice</option>
                <option value="other">Other Document</option>
              </select>
            </div>

            <div>
              <Label>File</Label>
              <div 
                className="mt-1 border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:bg-gray-50"
                onClick={() => fileInputRef.current?.click()}
              >
                {uploadFile ? (
                  <div className="flex items-center justify-center gap-2">
                    <FileText className="w-6 h-6 text-green-600" />
                    <span>{uploadFile.name}</span>
                  </div>
                ) : (
                  <div>
                    <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                    <p className="text-sm text-gray-500">Click to select file</p>
                    <p className="text-xs text-gray-400">PDF, Word, Excel (max 10MB)</p>
                  </div>
                )}
                <input
                  type="file"
                  ref={fileInputRef}
                  className="hidden"
                  accept=".pdf,.doc,.docx,.xls,.xlsx"
                  onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowUploadModal(false);
              setUploadFile(null);
            }}>
              Cancel
            </Button>
            <Button 
              onClick={handleUploadDocument}
              disabled={!uploadFile || uploading}
              style={{ backgroundColor: '#62D84E' }}
            >
              {uploading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Upload className="w-4 h-4 mr-2" />
              )}
              Upload
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Review & Comments Modal */}
      <Dialog open={showReviewModal} onOpenChange={setShowReviewModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <MessageSquare className="w-5 h-5 mr-2" style={{ color: '#62D84E' }} />
              Contract Review & Comments
            </DialogTitle>
            <DialogDescription>
              Review the contract, add comments, or request modifications
            </DialogDescription>
          </DialogHeader>

          {selectedContract && (
            <div className="space-y-4">
              {/* Contract Info */}
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium">{getContractTypeLabel(selectedContract.collaboration_type || selectedContract.template_type)}</span>
                  {getStatusBadge(selectedContract)}
                </div>
                {versions.length > 0 && (
                  <Badge variant="outline" className="mt-1">
                    <GitBranch className="w-3 h-3 mr-1" />
                    Version {versions.length + 1}
                  </Badge>
                )}
              </div>

              {/* Comments Section */}
              <div className="border rounded-lg p-4">
                <h4 className="font-medium mb-3 flex items-center">
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Comments ({comments.length})
                </h4>
                
                {comments.length > 0 ? (
                  <div className="space-y-3 max-h-48 overflow-y-auto mb-4">
                    {comments.map((comment, index) => (
                      <div 
                        key={index} 
                        className={`p-3 rounded-lg ${
                          comment.author_role === (isPM ? 'pm' : 'rm')
                            ? 'bg-blue-50 ml-6' 
                            : 'bg-gray-100 mr-6'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-gray-400" />
                            <span className="font-medium text-sm">{comment.author_name || comment.author_email}</span>
                            <Badge variant="outline" className="text-xs">
                              {comment.author_role?.toUpperCase()}
                            </Badge>
                          </div>
                          <span className="text-xs text-gray-400">
                            {comment.created_at ? new Date(comment.created_at).toLocaleString() : ''}
                          </span>
                        </div>
                        <p className="text-sm text-gray-700">{comment.content}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-gray-500 py-4 mb-4">No comments yet. Be the first to add one!</p>
                )}

                {/* Add Comment Form */}
                <div className="space-y-3 border-t pt-4">
                  <Textarea
                    placeholder="Add your comment, suggestion, or change request..."
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    rows={3}
                  />
                  <Button 
                    size="sm"
                    onClick={handleAddComment}
                    disabled={submittingReview || !newComment.trim()}
                    variant="outline"
                    className="w-full"
                  >
                    {submittingReview ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                    Send Comment
                  </Button>
                </div>
              </div>

              {/* Version History */}
              <div className="border rounded-lg">
                <button
                  className="w-full p-4 flex items-center justify-between text-left"
                  onClick={() => setShowHistory(!showHistory)}
                >
                  <h4 className="font-medium flex items-center">
                    <History className="w-4 h-4 mr-2" />
                    Version History
                  </h4>
                  {showHistory ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
                {showHistory && (
                  <div className="px-4 pb-4">
                    {versions.length > 0 ? (
                      <div className="space-y-2">
                        {versions.map((version, index) => (
                          <div key={index} className="p-2 bg-gray-50 rounded text-sm">
                            <div className="flex items-center justify-between">
                              <span className="font-medium">v{versions.length - index}</span>
                              <span className="text-xs text-gray-500">
                                {version.modified_by_name} • {new Date(version.created_at).toLocaleDateString()}
                              </span>
                            </div>
                            {version.change_summary && (
                              <p className="text-gray-600 mt-1">{version.change_summary}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-center text-gray-500 py-2">No previous versions</p>
                    )}
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={handleRequestModification}
                  disabled={submittingReview}
                  className="flex-1"
                >
                  <Edit3 className="w-4 h-4 mr-2" />
                  Request Modification
                </Button>
                <Button
                  onClick={handleApproveContract}
                  disabled={submittingReview}
                  className="flex-1"
                  style={{ backgroundColor: '#62D84E' }}
                >
                  {submittingReview ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Check className="w-4 h-4 mr-2" />
                  )}
                  Approve Contract
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContractWorkflowSection;
